
/* eslint-disable  @typescript-eslint/naming-convention */
import Vue, { PropType } from 'vue';
import { minLength, required } from 'vuelidate/lib/validators';
import Modal from '@/components/modal/Modal.vue';
import { Validation } from 'vuelidate/vuelidate';
import { LightingEdit, Room, Zone } from '@/interfaces/lighting';
import { DeviceEditData } from '@/interfaces/locations';
import { SelectMenu } from '@/interfaces/global';

interface DeviceForm {
	name: string;
	description: string;
	workingTimeSelect: { workingHoursStart: string; workingHoursEnd: string };
	workingDay: string[];
}

export default Vue.extend({
	components: {
		Modal,
	},
	data() {
		return {
			deviceForm: {
				name: '',
				description: '',
				workingTimeSelect: {
					workingHoursStart: '',
					workingHoursEnd: '',
				},
				workingDay: [],
			},
			selectedLocation: undefined,
			editMode: false,
		} as {
			deviceForm: DeviceForm;
			selectedLocation: Room | Zone | undefined;
			editMode: boolean;
		};
	},
	props: {
		deviceEditData: { type: Object as PropType<DeviceEditData> },
		rooms: { type: Array as PropType<Room[]> },
		zones: { type: Array as PropType<Zone[]> },
	},

	computed: {
		weekDayOptions(): SelectMenu[] {
			return [
				{ value: 'Mon', text: this.$t('Monday') },
				{ value: 'Tue', text: this.$t('Tuesday') },
				{ value: 'Wen', text: this.$t('Wednesday') },
				{ value: 'Thu', text: this.$t('Thursday') },
				{ value: 'Fri', text: this.$t('Friday') },
				{ value: 'Sat', text: this.$t('Saturday') },
				{ value: 'Sun', text: this.$t('Sunday') },
			];
		},
	},

	validations: {
		deviceForm: {
			name: {
				required,
				minLength: minLength(3),
			},
			description: {},
			workingDay: {},
			workingTimeSelect: {
				workingHoursStart: {
					required: function (): boolean {
						return !(
							this.deviceForm.workingTimeSelect.workingHoursStart &&
							!this.deviceForm.workingTimeSelect.workingHoursEnd
						);
					},
				},
				workingHoursEnd: {
					required: function (): boolean {
						return !(
							this.deviceForm.workingTimeSelect.workingHoursEnd &&
							!this.deviceForm.workingTimeSelect.workingHoursStart
						);
					},
				},
			},
		},
	},

	methods: {
		show(id?: number) {
			if (id) {
				this.editMode = true;
				this.selectedLocation =
					this.deviceEditData.deviceGroup === 'rooms'
						? this.rooms.find((room: Room) => room.id === id)
						: this.zones.find((zone: Zone) => zone.id === id);

				if (this.selectedLocation) {
					this.deviceForm.name = this.selectedLocation.name;
					this.deviceForm.description = this.selectedLocation.description;
					if (this.deviceEditData.deviceGroup === 'zones') {
						const startTime = this.selectedLocation?.working_time?.hours?.start_time ?? '';
						const endTime = this.selectedLocation?.working_time?.hours?.end_time ?? '';
						const days = this.selectedLocation?.working_time?.days ?? [];
						this.deviceForm.workingTimeSelect.workingHoursStart = startTime;
						this.deviceForm.workingTimeSelect.workingHoursEnd = endTime;
						this.deviceForm.workingDay = days;
					}
				}
			}
			this.$bvModal.show('modal-device-configuration');
		},
		hide() {
			this.$bvModal.hide('modal-device-configuration');
		},
		resetDeviceModal() {
			this.deviceForm = {
				name: '',
				description: '',
				workingTimeSelect: {
					workingHoursStart: '',
					workingHoursEnd: '',
				},
				workingDay: [],
			};
			this.editMode = false;
			this.$v.deviceForm.$reset();
		},

		async confirmDeviceModal() {
			this.$v.deviceForm.$touch();

			if (this.$v.deviceForm.$anyError) {
				return;
			}

			let newDevice: LightingEdit = {
				name: this.deviceForm.name,
				description: this.deviceForm.description,
				working_time: {
					days: this.deviceForm.workingDay,
					hours: {
						start_time: this.deviceForm.workingTimeSelect.workingHoursStart,
						end_time: this.deviceForm.workingTimeSelect.workingHoursEnd,
					},
				},
			};
			let storePath: string;
			if (this.selectedLocation) {
				storePath =
					this.deviceEditData.deviceGroup === 'rooms'
						? 'locations/rooms/modifyRoom'
						: 'locations/zones/modifyZone';
				this.$emit('changedDevice', storePath, newDevice, this.selectedLocation.id);
			} else {
				storePath =
					this.deviceEditData.deviceGroup === 'rooms'
						? 'locations/rooms/addRoom'
						: 'locations/zones/addZone';
				newDevice.fk_client = String(this.deviceEditData.parent.fk_client);
				newDevice.fk_site = String(this.deviceEditData.parent.fk_site);
				if (this.deviceEditData.deviceGroup === 'rooms') {
					newDevice.fk_area = String(this.deviceEditData.parent.id);
				} else {
					newDevice.fk_room = String(this.deviceEditData.parent.id);
				}
				this.$emit('newDevice', storePath, newDevice);
			}
			this.selectedLocation = undefined;
		},

		validateState(validation: Validation): boolean | null {
			const { $dirty, $error } = validation;
			return $dirty ? !$error : null;
		},

		closeDeviceModal(): void {
			this.editMode = false;
			this.selectedLocation = undefined;
			this.$bvModal.hide('modal-device-configuration');
		},

		modalTitle(deviceCategory: string, parentName: string) {
			if (this.editMode) {
				return this.$t('Edit {location}', { location: this.deviceForm.name });
			}
			switch (deviceCategory) {
				case 'rooms':
					return this.$t('Add a room to {parent}', { parent: parentName });
				case 'zones':
					return this.$t('Add a zone to {parent}', { parent: parentName });
				default:
					return this.$t('Add a {type} to {parent}', {
						parent: parentName,
						type: deviceCategory.slice(0, -1),
					});
			}
		},
	},
});
